import axios from "axios";

const ADMIN_API_BASE_URL = "/ofc/api/v1/debit-accounts/admin";
const PUBLIC_API_BASE_URL = "/ofc/api/v1/debit-accounts";

// Configure axios defaults
axios.defaults.withCredentials = true;

export interface ReimbursementCategory {
  id?: number;
  englishName?: string;
  fullwidthName?: string;
  displayOrder?: number;
  isActive?: boolean;
  createdAt?: string;
  updatedAt?: string;
  updatedBy?: string;
}

export interface ApiResponse<T> {
  success: boolean;
  message: string;
  data: T;
  error?: string;
}

class ReimbursementCategoryApiService {
  // ========== PUBLIC ENDPOINTS ==========

  /**
   * Get all active reimbursement categories (Authenticated users only)
   */
  async getAllActiveCategories(): Promise<ReimbursementCategory[]> {
    const response =
      await axios.get<ApiResponse<ReimbursementCategory[]>>(
        PUBLIC_API_BASE_URL,
      );
    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.error || "Failed to fetch categories");
    }
    return response.data.data;
  }

  // ========== ADMIN ENDPOINTS ==========

  /**
   * Get all categories including inactive (Admin only)
   */
  async getAllCategories(): Promise<ReimbursementCategory[]> {
    const response = await axios.get<ApiResponse<ReimbursementCategory[]>>(
      `${ADMIN_API_BASE_URL}/all`,
    );
    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.error || "Failed to fetch categories");
    }
    return response.data.data;
  }

  /**
   * Get a specific category by ID (Admin only)
   */
  async getCategoryById(id: number): Promise<ReimbursementCategory> {
    const response = await axios.get<ApiResponse<ReimbursementCategory>>(
      `${ADMIN_API_BASE_URL}/${id}`,
    );
    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.error || "Failed to fetch category");
    }
    return response.data.data;
  }

  /**
   * Create a new category (Admin only)
   */
  async createCategory(
    category: ReimbursementCategory,
  ): Promise<ReimbursementCategory> {
    const response = await axios.post<ApiResponse<ReimbursementCategory>>(
      ADMIN_API_BASE_URL,
      category,
    );
    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.error || "Failed to create category");
    }
    return response.data.data;
  }

  /**
   * Update an existing category (Admin only)
   */
  async updateCategory(
    id: number,
    category: ReimbursementCategory,
  ): Promise<ReimbursementCategory> {
    const response = await axios.put<ApiResponse<ReimbursementCategory>>(
      `${ADMIN_API_BASE_URL}/${id}`,
      category,
    );
    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.error || "Failed to update category");
    }
    return response.data.data;
  }

  /**
   * Delete a category (Admin only)
   */
  async deleteCategory(id: number): Promise<void> {
    const response = await axios.delete<ApiResponse<void>>(
      `${ADMIN_API_BASE_URL}/${id}`,
    );
    if (!response.data.success) {
      throw new Error(response.data.error || "Failed to delete category");
    }
  }

  /**
   * Reorder categories (Admin only)
   */
  async reorderCategories(
    categoryIds: number[],
  ): Promise<ReimbursementCategory[]> {
    const response = await axios.post<ApiResponse<ReimbursementCategory[]>>(
      `${ADMIN_API_BASE_URL}/reorder`,
      categoryIds,
    );
    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.error || "Failed to reorder categories");
    }
    return response.data.data;
  }
}

export default new ReimbursementCategoryApiService();
